import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { EntitlementsProvider } from "@/lib/entitlements";
import EnvBanner from "@/components/EnvBanner";
import { initKioskShell } from "@/lib/kioskShell";
import "@/App.css";

// Pantallas de operación: van en el paquete principal para que la caja, el autoservicio
// y la pantalla de cocina abran de inmediato (sin esperar la descarga del panel web).
import Login from "@/pages/Login";
import RecuperarClave from "./pages/RecuperarClave";
import MobileApp from "./mobile/MobileApp";
import MobileUsers from "./pages/admin/MobileUsers";
import AuthCallback from "@/pages/AuthCallback";
import POS from "@/pages/POS";
import Kiosk from "@/pages/Kiosk";
import PosPinLogin from "@/pages/PosPinLogin";
import KioskBootstrap from "@/pages/KioskBootstrap";

// El resto del sistema se descarga sólo cuando se entra a la pantalla: así el arranque
// de AiPos Caja no tiene que bajar el panel de administración completo.
const Landing = lazy(() => import("@/pages/Landing"));
const Register = lazy(() => import("@/pages/Register"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const Products = lazy(() => import("@/pages/admin/Products"));
const Categories = lazy(() => import("@/pages/admin/Categories"));
const Inventory = lazy(() => import("@/pages/admin/Inventory"));
const StockCritico = lazy(() => import("@/pages/admin/StockCritico"));
const PriceHistory = lazy(() => import("@/pages/admin/PriceHistory"));
const RestrictedReport = lazy(() => import("@/pages/admin/RestrictedReport"));
const ExpiryControl = lazy(() => import("@/pages/admin/ExpiryControl"));
const Stores = lazy(() => import("@/pages/admin/Stores"));
const BranchCatalog = lazy(() => import("@/pages/admin/BranchCatalog"));
const Sales = lazy(() => import("@/pages/admin/Sales"));
const PaymentsAudit = lazy(() => import("@/pages/admin/PaymentsAudit"));
const Cash = lazy(() => import("@/pages/admin/Cash"));
const Settings = lazy(() => import("@/pages/admin/Settings"));
const SalesReport = lazy(() => import("@/pages/admin/SalesReport"));
const Shop = lazy(() => import("@/pages/Shop"));
const Users = lazy(() => import("@/pages/admin/Users"));
const Orders = lazy(() => import("@/pages/admin/Orders"));
const Promotions = lazy(() => import("@/pages/admin/Promotions"));
const Delivery = lazy(() => import("@/pages/admin/Delivery"));
const Coupons = lazy(() => import("@/pages/admin/Coupons"));
const Subscriptions = lazy(() => import("@/pages/admin/Subscriptions"));
const MyCompanies = lazy(() => import("@/pages/admin/MyCompanies"));
const Quotes = lazy(() => import("@/pages/admin/Quotes"));
const HelpCenter = lazy(() => import("@/pages/admin/HelpCenter"));
const HelpAdmin = lazy(() => import("@/pages/admin/HelpAdmin"));
const LoyaltyAdmin = lazy(() => import("@/pages/admin/LoyaltyAdmin"));
const ContestsAdmin = lazy(() => import("@/pages/admin/ContestsAdmin"));
const TerminalsAdmin = lazy(() => import("@/pages/admin/TerminalsAdmin"));
const PaymentMethodsAdmin = lazy(() => import("@/pages/admin/PaymentMethodsAdmin"));
const ValesCambio = lazy(() => import("@/pages/admin/ValesCambio"));
const SuperadminKiosks = lazy(() => import("@/pages/admin/SuperadminKiosks"));
const HardwareAdmin = lazy(() => import("@/pages/admin/HardwareAdmin"));
const AiInsights = lazy(() => import("@/pages/admin/AiInsights"));
const StoresReport = lazy(() => import("@/pages/admin/StoresReport"));
const TecnicoConsole = lazy(() => import("@/pages/admin/TecnicoConsole"));
const MyTickets = lazy(() => import("@/pages/admin/MyTickets"));
const MyClients = lazy(() => import("@/pages/admin/MyClients"));
const SecondScreen = lazy(() => import("@/pages/SecondScreen"));
const PurchasesXml = lazy(() => import("@/pages/admin/PurchasesXml"));
const BoletaPreview = lazy(() => import("@/pages/BoletaPreview"));
const SiiConfig = lazy(() => import("@/pages/admin/SiiConfig"));
const FoliosCaf = lazy(() => import("@/pages/admin/FoliosCaf"));
const LogFolios = lazy(() => import("@/pages/admin/LogFolios"));
const ProyeccionCobros = lazy(() => import("@/pages/admin/ProyeccionCobros"));
const PagosPlanes = lazy(() => import("@/pages/admin/PagosPlanes"));
const CompraInteligente = lazy(() => import("@/pages/admin/CompraInteligente"));
const SiiReadiness = lazy(() => import("@/pages/admin/SiiReadiness"));
const SellersReport = lazy(() => import("@/pages/admin/SellersReport"));
const DtesList = lazy(() => import("@/pages/admin/DtesList"));
const LibroSii = lazy(() => import("@/pages/admin/LibroSii"));
const DocumentosCarta = lazy(() => import("@/pages/admin/DocumentosCarta"));
const GuiasFacturables = lazy(() => import("@/pages/admin/GuiasFacturables"));
const ReporteDescuentos = lazy(() => import("@/pages/admin/ReporteDescuentos"));
const ReporteTickets = lazy(() => import("@/pages/admin/ReporteTickets"));
const ReporteClientes = lazy(() => import("@/pages/admin/ReporteClientes"));
const AvisosCaja = lazy(() => import("@/pages/admin/AvisosCaja"));
const ReporteMovimientosCaja = lazy(() => import("@/pages/admin/ReporteMovimientosCaja"));
const ReporteVentasNulas = lazy(() => import("@/pages/admin/ReporteVentasNulas"));
const ReporteCupones = lazy(() => import("@/pages/admin/ReporteCupones"));
const ReporteTarjetaPropia = lazy(() => import("@/pages/admin/ReporteTarjetaPropia"));
const TarjetaClave = lazy(() => import("@/pages/TarjetaClave"));
const TarjetaWallet = lazy(() => import("@/pages/TarjetaWallet"));
const PresalesAdmin = lazy(() => import("@/pages/admin/PresalesAdmin"));
const BalanceLabelsAdmin = lazy(() => import("@/pages/admin/BalanceLabelsAdmin"));
const SuperadminTools = lazy(() => import("@/pages/admin/SuperadminTools"));
const LegalAdmin = lazy(() => import("@/pages/admin/LegalAdmin"));
const RepresentantesAdmin = lazy(() => import("@/pages/admin/RepresentantesAdmin"));
const Representantes = lazy(() => import("@/pages/Representantes"));
const RepresentantePanel = lazy(() => import("@/pages/RepresentantePanel"));
const RepresentanteClave = lazy(() => import("@/pages/RepresentanteClave"));
const ReferidosAdmin = lazy(() => import("@/pages/admin/ReferidosAdmin"));
const RentabilidadAdmin = lazy(() => import("@/pages/admin/RentabilidadAdmin"));
const DbAdmin = lazy(() => import("@/pages/admin/DbAdmin"));
const Respaldos = lazy(() => import("@/pages/admin/Respaldos"));
const PanelProduccion = lazy(() => import("@/pages/admin/PanelProduccion"));
const EmisorDte = lazy(() => import("@/pages/admin/EmisorDte"));
const ClientesAdmin = lazy(() => import("@/pages/admin/ClientesAdmin"));
const ProveedoresAdmin = lazy(() => import("@/pages/admin/ProveedoresAdmin"));
const ChoferesAdmin = lazy(() => import("@/pages/admin/ChoferesAdmin"));
const TrialsConsole = lazy(() => import("@/pages/admin/TrialsConsole"));
const Reconciliation = lazy(() => import("@/pages/admin/Reconciliation"));
const EmissionPolicy = lazy(() => import("@/pages/admin/EmissionPolicy"));
const CycleCountsAdmin = lazy(() => import("@/pages/admin/CycleCountsAdmin"));
const TransfersAdmin = lazy(() => import("@/pages/admin/TransfersAdmin"));
const PriceCheck = lazy(() => import("@/pages/PriceCheck"));
const Pair = lazy(() => import("@/pages/Pair"));
const UserPinsAdmin = lazy(() => import("@/pages/admin/UserPinsAdmin"));
const AccessLogs = lazy(() => import("@/pages/admin/AccessLogs"));
const Dispositivos = lazy(() => import("@/pages/admin/Dispositivos"));
const Kds = lazy(() => import("@/pages/Kds"));
const Journal = lazy(() => import("@/pages/admin/Journal"));
const OperationLog = lazy(() => import("@/pages/admin/OperationLog"));
const Pda = lazy(() => import("@/pages/Pda"));
const ConsultaMediaAdmin = lazy(() => import("@/pages/admin/ConsultaMediaAdmin"));
const HardwareStore = lazy(() => import("@/pages/HardwareStore"));
const HardwarePaymentResult = lazy(() => import("@/pages/HardwarePaymentResult"));
const PublicOrder = lazy(() => import("@/pages/PublicOrder"));
const Plans = lazy(() => import("@/pages/Plans"));
const LogoPicker = lazy(() => import("@/pages/LogoPicker"));
const PaymentResult = lazy(() => import("@/pages/PaymentResult"));

/** Carga de pantalla: breve y con la identidad de AiPos (nunca una página en blanco). */
function Cargando() {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50" data-testid="app-loading">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin" />
        <p className="text-xs font-semibold tracking-widest text-slate-400 uppercase">Cargando</p>
      </div>
    </div>
  );
}

function NoRep({ children }) {
  const { user } = useAuth();
  return user?.role === "REPRESENTANTE" ? <Navigate to="/representante" replace /> : children;
}

function SoloRep({ children }) {
  const { user } = useAuth();
  return user?.role === "REPRESENTANTE" || (user?.role === "TECNICO" && user?.rep_access) ? children : <Navigate to="/admin" replace />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Cargando />;
  if (!user) {
    // Caja ya emparejada: se entra con PIN del cajero, nunca con correo y contraseña
    if (localStorage.getItem("aipos_device_token") && location.pathname.startsWith("/pos")) {
      return <Navigate to="/pos-pin" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  return children;
}

function HardwareAdminRoute() {
  const { user } = useAuth();
  return <HardwareAdmin role={user?.role} />;
}

function SuperadminKiosksRoute() {
  const { user } = useAuth();
  return <SuperadminKiosks role={user?.role} />;
}

// La pantalla de cocina entra con login o, si el equipo está emparejado como KDS, sin login
function KdsRoute() {
  const esDispositivoKds = localStorage.getItem("aipos_device_token")
    && localStorage.getItem("aipos_device_tipo") === "KDS";
  return esDispositivoKds ? <Kds /> : <Protected><Kds /></Protected>;
}

function AppRoutes() {
  const location = useLocation();
  initKioskShell();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<Cargando />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/recuperar-clave" element={<RecuperarClave />} />
        <Route path="/register" element={<Register />} />
        <Route path="/pos" element={<Protected><POS /></Protected>} />
        <Route path="/kds" element={<KdsRoute />} />
        <Route path="/autoservicio" element={<Protected><Kiosk /></Protected>} />
        <Route path="/tienda/:companyId" element={<Shop />} />
        <Route path="/pedido/:saleId" element={<PublicOrder />} />
        <Route path="/tarjeta/clave/:token" element={<TarjetaClave />} />
        <Route path="/tarjeta/:viewToken" element={<TarjetaWallet />} />
        <Route path="/boleta/:sale_id" element={<BoletaPreview />} />
        <Route path="/planes" element={<Plans />} />
        <Route path="/representantes" element={<Representantes />} />
        <Route path="/representante/clave" element={<RepresentanteClave />} />
        <Route path="/registro" element={<Register />} />
        <Route path="/representante" element={<Protected><SoloRep><RepresentantePanel /></SoloRep></Protected>} />
        <Route path="/logos" element={<LogoPicker />} />
        <Route path="/pago/resultado" element={<PaymentResult />} />
        <Route path="/equipos" element={<HardwareStore />} />
        <Route path="/equipos/pago-resultado" element={<HardwarePaymentResult />} />
        <Route path="/k/:slug" element={<KioskBootstrap />} />
        <Route path="/consulta/:slug" element={<PriceCheck />} />
        <Route path="/pair" element={<Pair />} />
        <Route path="/pos-pin" element={<PosPinLogin />} />
        <Route path="/movil/*" element={<MobileApp />} />
        <Route path="/pda" element={<Pda />} />
        <Route path="/second-screen" element={<SecondScreen />} />
        <Route path="/admin" element={<Protected><NoRep><AdminLayout /></NoRep></Protected>}>
          <Route index element={<Dashboard />} />
          <Route path="productos" element={<Products />} />
          <Route path="categorias" element={<Categories />} />
          <Route path="familias" element={<Categories />} />
          <Route path="inventario" element={<Inventory />} />
          <Route path="stock-critico" element={<StockCritico />} />
          <Route path="historial-precios" element={<PriceHistory />} />
          <Route path="ventas-restringidas" element={<RestrictedReport />} />
          <Route path="vencimientos" element={<ExpiryControl />} />
          <Route path="tiendas" element={<Stores />} />
          <Route path="catalogo-sucursal" element={<BranchCatalog />} />
          <Route path="terminales" element={<TerminalsAdmin />} />
          <Route path="medios-pago" element={<PaymentMethodsAdmin />} />
          <Route path="vales-cambio" element={<ValesCambio />} />
          <Route path="ventas" element={<Sales />} />
          <Route path="auditoria-pagos" element={<PaymentsAudit />} />
          <Route path="pedidos" element={<Orders />} />
          <Route path="promociones" element={<Promotions />} />
          <Route path="cupones" element={<Coupons />} />
          <Route path="fidelizacion" element={<LoyaltyAdmin />} />
          <Route path="concursos" element={<ContestsAdmin />} />
          <Route path="despacho" element={<Delivery />} />
          <Route path="caja" element={<Cash />} />
          <Route path="usuarios" element={<Users />} />
          <Route path="usuarios-app-movil" element={<MobileUsers />} />
          <Route path="suscripcion" element={<Subscriptions />} />
          <Route path="empresas" element={<MyCompanies />} />
          <Route path="cotizaciones" element={<Quotes />} />
          <Route path="ayuda" element={<HelpCenter />} />
          <Route path="ayuda/:slug" element={<HelpCenter />} />
          <Route path="ayuda-admin" element={<HelpAdmin />} />
          <Route path="reportes-ia" element={<AiInsights />} />
          <Route path="reporte-sucursales" element={<StoresReport />} />
          <Route path="tecnico" element={<TecnicoConsole />} />
          <Route path="mis-tickets" element={<MyTickets />} />
          <Route path="mis-clientes" element={<MyClients />} />
          <Route path="compras-xml" element={<PurchasesXml />} />
          <Route path="sii" element={<SiiConfig />} />
          <Route path="folios" element={<FoliosCaf />} />
          <Route path="log-folios" element={<LogFolios />} />
          <Route path="proyeccion-cobros" element={<ProyeccionCobros />} />
          <Route path="pagos-planes" element={<PagosPlanes />} />
          <Route path="compra-inteligente" element={<CompraInteligente />} />
          <Route path="listo-facturar" element={<SiiReadiness />} />
          <Route path="vendedores" element={<SellersReport />} />
          <Route path="dtes" element={<DtesList />} />
          <Route path="libro-sii" element={<LibroSii />} />
          <Route path="documentos" element={<DocumentosCarta />} />
          <Route path="guias-facturables" element={<GuiasFacturables />} />
          <Route path="pre-ventas" element={<PresalesAdmin />} />
          <Route path="balanzas" element={<BalanceLabelsAdmin />} />
          <Route path="sa-tools" element={<SuperadminTools />} />
          <Route path="politicas-legales" element={<LegalAdmin />} />
          <Route path="representantes" element={<RepresentantesAdmin />} />
          <Route path="referidos" element={<ReferidosAdmin />} />
          <Route path="rentabilidad" element={<RentabilidadAdmin />} />
          <Route path="base-datos" element={<DbAdmin />} />
          <Route path="respaldos" element={<Respaldos />} />
          <Route path="produccion" element={<PanelProduccion />} />
          <Route path="emisor-dte" element={<EmisorDte />} />
          <Route path="clientes" element={<ClientesAdmin />} />
          <Route path="proveedores" element={<ProveedoresAdmin />} />
          <Route path="choferes" element={<ChoferesAdmin />} />
          <Route path="pruebas" element={<TrialsConsole />} />
          <Route path="conciliacion" element={<Reconciliation />} />
          <Route path="emision" element={<EmissionPolicy />} />
          <Route path="inventario-ciclico" element={<CycleCountsAdmin />} />
          <Route path="traspasos" element={<TransfersAdmin />} />
          <Route path="pins-usuarios" element={<UserPinsAdmin />} />
          <Route path="accesos" element={<AccessLogs />} />
          <Route path="dispositivos" element={<Dispositivos />} />
          <Route path="journal" element={<Journal />} />
          <Route path="operaciones" element={<OperationLog />} />
          <Route path="consulta-media" element={<ConsultaMediaAdmin />} />
          <Route path="tienda-equipos" element={<HardwareAdminRoute />} />
          <Route path="clientes-kioscos" element={<SuperadminKiosksRoute />} />
          <Route path="reporte-familias" element={<SalesReport dim="familia" />} />
          <Route path="reporte-vendedores" element={<SalesReport dim="vendedor" />} />
          <Route path="reporte-productos" element={<SalesReport dim="producto" />} />
          <Route path="reporte-sin-venta" element={<SalesReport dim="sin_venta" key="sin_venta" />} />
          <Route path="reporte-medios-pago" element={<SalesReport dim="medio_pago" />} />
          <Route path="reporte-descuentos" element={<ReporteDescuentos />} />
          <Route path="reporte-tickets" element={<ReporteTickets />} />
          <Route path="reporte-clientes" element={<ReporteClientes />} />
          <Route path="avisos-caja" element={<AvisosCaja />} />
          <Route path="reporte-movimientos-caja" element={<ReporteMovimientosCaja />} />
          <Route path="reporte-ventas-nulas" element={<ReporteVentasNulas />} />
          <Route path="reporte-cupones" element={<ReporteCupones />} />
          <Route path="reporte-tarjeta-propia" element={<ReporteTarjetaPropia />} />
          <Route path="reporte-utilidad" element={<SalesReport dim="utilidad" />} />
          <Route path="configuracion" element={<Settings />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

// En la caja los avisos van al centro: arriba a la derecha tapaban el botón "Funciones"
function PosAwareToaster() {
  const { pathname } = useLocation();
  const enCaja = pathname.startsWith("/pos");
  return <Toaster richColors position={enCaja ? "top-center" : "top-right"} />;
}

export default function App() {  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <EntitlementsProvider>
            <EnvBanner />
            <AppRoutes />
            <PosAwareToaster />
          </EntitlementsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
